<template>
  <div>
    <div class="d-flex justify-space-between">
      <div class="my-6 d-flex align-center">
        <BaseAvatar :size="78" class="mr-6 flex-shrink-0" :src="logo" />
        <BaseTitle v-text="welcomeText" />
      </div>
    </div>
    <!-- NO QUESTION TEMPLATE SECTION -->
    <div v-if="!activeOrganization.hasQuestionTemplates" class="alert_item">
      <div class="icon">
        <BaseIcon icon="$mdiAlertCircle" />
      </div>
      <div class="item_message">
        <div>
          <i18n path="empty_qstd.message_1">
            <template #orga_name>
              <strong v-text="activeOrganization.name" />
            </template>
          </i18n>
        </div>
        <div>
          <i18n path="empty_qstd.message_2">
            <template #create_link>
              <router-link
                :to="{
                  name: 'QUESTION_TEMPLATE',
                  params: {
                    organizationId: activeOrganization.id,
                  },
                }"
                v-text="$t('empty_qstd.create_link')"
              />
            </template>
          </i18n>
        </div>
      </div>
    </div>
    <!-- NOTIFICAITON SECTION -->
    <div class="notifications_container">
      <BaseTitle class="d-block" v-text="$t('utils.notifications')" />
      <div class="filter-container">
        <div
          class="filter-title"
          @click.stop="isFilterSectionOpen = !isFilterSectionOpen"
        >
          <BaseIcon
            :icon="isFilterSectionOpen ? '$mdiMenuUp' : '$mdiMenuDown'"
            color="primary"
          />
          <span class="filter">{{ $t("utils.filters") }}</span>
          <div class="separator" />
        </div>
        <template v-if="isFilterSectionOpen">
          <div class="filter-items">
            <div
              v-for="filter in filters"
              :key="filter.key"
              class="filter-item"
            >
              <div class="filter-label-text">{{ filter.label }}</div>
              <template v-if="filter.component == 'select'">
                <BaseSelect
                  v-model="filter.value"
                  :items="filter.items"
                  item-text="label"
                  item-value="type"
                  solo
                  @change="fetchNotifications"
                />
              </template>
              <template v-if="filter.component == 'input'">
                <BaseTextField
                  v-model="filter.value"
                  @input="handleFolderNameSearch"
                />
              </template>
            </div>
          </div>
          <div class="separator" />
        </template>
      </div>
      <div class="notifications">
        <div
          v-for="(notification, i) in notifications.items"
          :key="`n${i}`"
          class="notification_item"
        >
          <div class="icon">
            <BaseIcon :icon="notification.icon" color="grey" />
          </div>
          <i18n class="noitification_content" :path="notification.content">
            <template #folder_name>
              <router-link
                :to="{
                  name: 'folderDetail',
                  params: {
                    organizationId: $route.params.organizationId,
                    folderId: notification.folder.id,
                  },
                }"
                v-text="`#${notification.folder.name}`"
              />
            </template>
          </i18n>
          <div class="time_ago" v-text="notification.relativeDateCreated" />
        </div>
        <template v-if="notifications.loading">
          <v-skeleton-loader class="notification_skeleton" type="text" />
          <v-skeleton-loader class="notification_skeleton" type="text" />
          <v-skeleton-loader class="notification_skeleton" type="text" />
          <v-skeleton-loader class="notification_skeleton" type="text" />
        </template>
        <div v-else-if="notifications.next" class="actions">
          <BaseButton type="primary" @click="loadMoreNotifications()">
            {{ $t("utils.show_more") }}
          </BaseButton>
        </div>
        <div
          v-if="!notifications.loading && !notifications.items.length"
          class="empty_notification"
        >
          <div class="icon">
            <BaseIcon icon="$mdiBellAlertOutline" />
          </div>
          <div class="notification_label" v-text="$t('notifications.empty')" />
        </div>
      </div>
    </div>
    <!-- FOLDERS TO REVIEW -->
    <div class="section_container">
      <BaseTitle class="d-block" v-text="$t('folders_to_review.title')" />
      <div class="folders_to_review">
        <div
          v-for="(folder, i) in foldersToReview.items"
          :key="`n${i}`"
          class="folder_item"
        >
          <div class="icon">
            <BaseIcon icon="$mdiFolderAlertOutline" />
          </div>
          <div class="item_message">
            <i18n path="folders_to_review.item_message">
              <template #folder_name>
                <router-link
                  :to="{
                    name: 'folderDetail',
                    params: {
                      organizationId: $route.params.organizationId,
                      folderId: folder.id,
                    },
                  }"
                  v-text="`#${folder.name}`"
                />
              </template>
            </i18n>
            <span
              class="days"
              v-text="
                $tc(
                  'folders_to_review.days',
                  folder.workflow.inactivityDaysLimit,
                  {
                    days: folder.workflow.inactivityDaysLimit,
                  }
                )
              "
            />
          </div>
        </div>
        <template v-if="foldersToReview.loading">
          <v-skeleton-loader
            v-for="n in 5"
            :key="n"
            class="skeleton"
            type="text"
          />
        </template>
        <div v-else-if="foldersToReview.next" class="actions">
          <BaseButton type="primary" @click="loadMoreFolderToReview()">
            {{ $t("utils.show_more") }}
          </BaseButton>
        </div>
        <div
          v-if="!foldersToReview.loading && !foldersToReview.items.length"
          class="empty_section"
        >
          <div class="icon">
            <BaseIcon icon="$mdiBellAlertOutline" />
          </div>
          <div class="empty_label" v-text="$t('folders_to_review.no_data')" />
        </div>
      </div>
    </div>
    <OrganizationDashboardStatistics />
    <OrganizationDashboardCourses />
  </div>
</template>

<script>
import OrganizationDashboardCourses from "../Components/OrganizationDashboard/OrganizationDashboardCourses";
import OrganizationDashboardStatistics from "../Components/OrganizationDashboard/OrganizationDashboardStatistics";
import OrganizationAPI from "@/core/Services/organization.api";
import { Notification } from "@/core/Models/Notification";
import { mapState } from "vuex";
export default {
  name: "OrganizationDashboard",
  components: {
    OrganizationDashboardCourses,
    OrganizationDashboardStatistics,
  },
  data() {
    return {
      organization: null,
      isFetchingOrganization: false,
      collaborators: {
        loading: true,
        items: [],
      },
      notifications: {
        loading: true,
        page: 1,
        count: 0,
        next: 0,
        pageSize: 10,
        items: [],
      },
      foldersToReview: {
        loading: true,
        page: 1,
        count: 0,
        next: 0,
        pageSize: 10,
        items: [],
      },
      filters: [
        {
          key: "type",
          label: this.$t("utils.eventType"),
          value: null,
          component: "select",
          items: [],
        },
        {
          key: "folder_name",
          label: this.$t("folder.folderName"),
          value: null,
          component: "input",
          items: [],
        },
      ],
      isFilterSectionOpen: false,
      searchTimeoutRef: null,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.account.user,
      activeOrganization: (state) => state.organization.activeOrganization,
    }),
    logo() {
      return this.activeOrganization?.logo;
    },
    welcomeText() {
      return `${this.$t("organization.welcomeTitle")} ${
        this.activeOrganization?.name
      }`;
    },
  },
  created() {
    this.fetchNotifications();
    this.fetchFoldersToReview();
    this.fetchNotificationEvent();
  },
  methods: {
    async fetchNotificationEvent() {
      const response = await OrganizationAPI.getNotificationsType();
      const result = response.map((notif) => ({
        ...notif,
        label: this.$t(`notifications.label.${notif.type.toLocaleLowerCase()}`),
      }));
      this.filters.find((filter) => filter.key == "type").items = [
        { type: "", label: "------------------------------" },
        ...result,
      ];
    },
    handleFolderNameSearch() {
      if (this.searchTimeoutRef) clearTimeout(this.searchTimeoutRef);
      this.searchTimeoutRef = setTimeout(async () => {
        this.fetchNotifications();
      }, 500);
    },
    setActiveFilters(filters) {
      const filterChecked = filters.filter((filter) => !!filter.value);
      let filterObject = {};
      for (let filter of filterChecked) {
        filterObject[filter.key] = filter.value;
      }

      return filterObject;
    },
    async fetchNotifications(type) {
      this.notifications.loading = true;
      try {
        const filter = this.setActiveFilters(this.filters);
        const res = await OrganizationAPI.getOrganizationNotifications({
          organizationId: this.$route.params.organizationId,
          params: {
            page: this.notifications.page,
            page_size: this.notifications.pageSize,
            ...filter,
          },
        });
        if (type === "LOAD_MORE") {
          this.notifications.items = [
            ...this.notifications.items,
            ...res.data.results.map((d) => new Notification(d)),
          ];
        } else {
          this.notifications.items = [
            ...res.data.results.map((d) => new Notification(d)),
          ];
        }

        this.notifications.count = res.data.count;
        this.notifications.next = res.data.next;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.notifications.loading = false;
    },
    loadMoreNotifications() {
      this.notifications.page += 1;
      this.fetchNotifications("LOAD_MORE");
    },
    async fetchFoldersToReview(loadMore = false) {
      this.foldersToReview.loading = true;
      try {
        const res = await OrganizationAPI.getFoldersToReview({
          params: {
            organization: this.$route.params.organizationId,
            page: this.foldersToReview.page,
            page_size: this.foldersToReview.pageSize,
          },
        });
        if (loadMore) {
          this.foldersToReview.items = [
            ...this.foldersToReview.items,
            ...res.data.results,
          ];
        } else {
          this.foldersToReview.items = [...res.data.results];
        }

        this.foldersToReview.count = res.data.count;
        this.foldersToReview.next = res.data.next;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.foldersToReview.loading = false;
    },
    loadMoreFolderToReview() {
      this.foldersToReview.page += 1;
      this.fetchFoldersToReview(true);
    },
  },
};
</script>

<style scoped lang="scss">
.notifications_container {
  background: #fff;
  margin-bottom: 24px;
  padding: 24px;
  .filter-container {
    display: block;
    margin-top: 16px;
    .filter-title {
      display: flex;
      align-items: center;
      color: #2536cc;
      font-size: 16px;
      cursor: pointer;
    }
    .filter-items {
      margin: 24px 0 24px 24px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      ::v-deep {
        .v-text-field__details {
          display: none;
        }
      }

      .filter-item {
        display: flex;
        gap: 8px;
        height: 40px;
        align-items: center;
        max-width: 500px;
        .filter-label-text {
          min-width: 150px;
        }
      }
    }
  }

  .notifications {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;
    .notification_item {
      display: flex;
      align-items: center;
      padding: 16px;
      border-radius: 5px;
      background: #e6eff4;
      gap: 16px;
      .icon {
        width: 32px;
        height: 32px;
        border-radius: 5px;
        background: #f5f5f5;
        display: flex;
        justify-content: center;
        align-items: center;
        ::v-deep {
          .v-icon {
            width: 20px;
            height: 20px;
          }
        }
      }
      .noitification_content {
        font-size: 14px;
        line-height: 20px;
        color: #333333;
        flex: 1;
      }
      .time_ago {
        font-size: 12px;
        line-height: 16px;
        color: #6e758c;
      }
    }
    .notification_skeleton {
      height: 64px;
      width: 100%;
      ::v-deep {
        .v-skeleton-loader__bone {
          height: 64px;
        }
      }
    }
    .actions {
      display: flex;
      justify-content: center;
    }
    .empty_notification {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      font-size: 14px;
      line-height: 20px;
      gap: 2px;
      color: #999;
      margin-bottom: 24px;
      .icon {
        ::v-deep {
          .v-icon {
            width: 128px;
            height: 128px;
            color: #afafaf;
          }
          svg {
            width: 128px;
            height: 128px;
          }
        }
      }
    }
  }
}

.section_container {
  background: #fff;
  margin-bottom: 24px;
  padding: 24px;

  .folders_to_review {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;
    .folder_item {
      display: flex;
      align-items: center;
      padding: 16px;
      border-radius: 5px;
      background: rgba(#ff5267, 0.1);
      gap: 16px;
      .icon {
        width: 32px;
        height: 32px;
        border-radius: 5px;
        background: #fff;
        display: flex;
        justify-content: center;
        align-items: center;
        ::v-deep {
          .v-icon {
            color: #ff5267;
            width: 20px;
            height: 20px;
          }
        }
      }
      .item_message {
        font-size: 14px;
        line-height: 20px;
        color: #333333;
        flex: 1;
        .days {
          font-weight: bold;
          color: #ff5267;
        }
      }
    }
    .skeleton {
      height: 64px;
      width: 100%;
      ::v-deep {
        .v-skeleton-loader__bone {
          height: 64px;
        }
      }
    }
    .actions {
      display: flex;
      justify-content: center;
    }
    .empty_section {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      font-size: 14px;
      line-height: 20px;
      gap: 2px;
      color: #999;
      margin-bottom: 24px;
      .icon {
        ::v-deep {
          .v-icon {
            width: 128px;
            height: 128px;
            color: #afafaf;
          }
          svg {
            width: 128px;
            height: 128px;
          }
        }
      }
    }
  }
}

.separator {
  width: 100%;
  border: 1px solid #2536cc;
}
.alert_item {
  display: flex;
  padding: 16px;
  border-radius: 5px;
  background: rgba(#ff5267, 0.1);
  gap: 16px;
  margin-bottom: 24px;
  .icon {
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    ::v-deep {
      .v-icon__svg {
        color: #ff5267;
        width: 32px;
        height: 32px;
        flex: none;
      }
    }
  }
  .item_message {
    font-size: 14px;
    line-height: 20px;
    color: #333333;
    flex: 1;
  }
}
</style>
